"use client";

import React, { useState, useEffect, useRef } from "react";
import { X, Star } from "lucide-react";
import { useReview } from "../context/ReviewContext";
import { CDN_URL } from "../config/apiconfig";
const ReviewModal = ({ isOpen, onClose, review, onSave }) => {
  const { addReview, updateReview, refetch, products } = useReview();
const [showDropdown, setShowDropdown] = useState(false);
  const [formData, setFormData] = useState({
    customerName: "",
    customerAvatar: "", // existing image name/url from API
    avatarFile: null,   // new file selected from input
    productId: "",
    rating: 5,
    comment: "",
    status: "Active",
    helpful: 0,
    notHelpful: 0,
  });

  const [previewUrl, setPreviewUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (review) {
      setFormData({
        customerName: review.user_name ?? review.user_id ?? "Guest User",
        customerAvatar: review.user_image ?? review.image ?? "",
        avatarFile: null,
        productId: String(review.product_id ?? ""),
        rating: parseInt(review.rating) || 5,
        comment: review.description ?? "",
        status: review.status ?? "Active",
        helpful: review.helpful ?? 0,
        notHelpful: review.notHelpful ?? 0,
      });
setPreviewUrl(
  review?.user_image
    ? `${CDN_URL}/${review.user_image}`
    : review?.image
    ? `${CDN_URL}/${review.image}`
    : ""
);
    } else {
      setFormData({
        customerName: "",
        customerAvatar: "",
        avatarFile: null,
        productId: "",
        rating: 5,
        comment: "",
        status: "Active",
        helpful: 0,
        notHelpful: 0,
      });

      setPreviewUrl("");
    }

    setErrors({});
  }, [review, isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    console.log("Products inside modal:", products);
  }, [products]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;

    setFormData((prev) => ({
      ...prev,
      avatarFile: file,
    }));

    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(review?.user_image ?? review?.image ?? "");
    }

    if (errors.avatarFile) {
      setErrors((prev) => ({
        ...prev,
        avatarFile: "",
      }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));

    if (errors.rating) {
      setErrors((prev) => ({
        ...prev,
        rating: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = "Customer name is required";
    }

    if (!formData.productId) {
      newErrors.productId = "Please select a product";
    }

    if (!formData.comment.trim()) {
      newErrors.comment = "Review comment is required";
    } else if (formData.comment.trim().length < 10) {
      newErrors.comment = "Comment must be at least 10 characters";
    }

    if (formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = "Rating must be between 1 and 5";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) return;

  setIsLoading(true);

  try {
    const payload = new FormData();

    payload.append("user_name", formData.customerName);
    payload.append("product_id", formData.productId);
    payload.append("rating", formData.rating);
    payload.append("description", formData.comment);
    payload.append("status", formData.status);
    payload.append("create_audit_id", "1");
    payload.append("update_audit_id", "1");

    if (formData.avatarFile) {
      payload.append("image", formData.avatarFile);
    }

    console.log("Submitting review...");

    if (review) {
      await updateReview(review.id, payload);
    } else {
      await addReview(payload);
    }

    await refetch();
    onClose();
  } catch (error) {
    console.error("Error saving review:", error);
    setErrors({ general: "Failed to save review. Please try again." });
  } finally {
    setIsLoading(false);
  }
};

  const renderStars = (rating, interactive = false) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-6 h-6 cursor-pointer transition-colors ${
          index < rating
            ? "text-yellow-400 fill-current"
            : "text-gray-300 hover:text-yellow-300"
        }`}
        onClick={interactive ? () => handleRatingChange(index + 1) : undefined}
      />
    ));
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-2xl bg-gradient-to-br from-orange-50 to-orange-200 bg-opacity-95 shadow-2xl z-50 transform transition-all duration-300 ease-out overflow-y-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ borderRadius: "10px 0 0 10px" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <button
          onClick={onClose}
          className="fixed left-4 top-4 w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white z-50 flex items-center justify-center shadow-lg"
          aria-label="Close review modal"
          disabled={isLoading}
        >
          <X className="w-6 h-6" />
        </button>

        <div className="sticky top-0 bg-orange-50 p-8 z-10 border-b border-orange-200">
          <div className="text-center">
            <h2 id="modal-title" className="text-3xl font-bold text-teal-800 mb-2">
              {review ? "Edit Review" : "Add New Review"}
            </h2>
            <div className="w-16 h-1 bg-orange-300 rounded-full mx-auto"></div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-8 space-y-8 min-h-[700px] rounded-[25px] m-4 bg-gray-400 bg-opacity-20"
        >
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          <div className="space-y-3">
            <label
              htmlFor="customerName"
              className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"
            >
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Customer Name *
            </label>
            <input
              type="text"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={handleInputChange}
              placeholder="Enter customer name..."
              className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 ${
                errors.customerName
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-orange-200"
              }`}
              disabled={isLoading}
            />
            {errors.customerName && (
              <p className="text-red-500 text-sm">{errors.customerName}</p>
            )}
          </div>

          <div className="space-y-3">
            <label
              htmlFor="customerAvatar"
              className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"
            >
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Customer Image
            </label>

            <input
              type="file"
              id="customerAvatar"
              name="customerAvatar"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800"
              disabled={isLoading}
            />

            {previewUrl && (
              <div className="mt-3">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-20 h-20 object-cover rounded-xl border border-orange-200"
                />
              </div>
            )}
          </div>

          <div className="space-y-3">
            <label
              htmlFor="productId"
              className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"
            >
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Product *
            </label>
        <div className="relative">
  {/* Selected value */}
  <div
    onClick={() => setShowDropdown(!showDropdown)}
    className={`w-full px-3 py-2 text-sm bg-white bg-opacity-80 border rounded-lg 
      cursor-pointer flex justify-between items-center ${
        errors.productId
          ? "border-red-300"
          : "border-orange-200"
      }`}
  >
    <span className="truncate">
      {products.find(p => String(p.id) === formData.productId)?.product_name || "Select a product..."}
    </span>
    <span className="text-xs">▼</span>
  </div>

  {/* Dropdown */}
  {showDropdown && (
    <div className="absolute z-50 w-full mt-1 bg-white border border-orange-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
      {products
        ?.filter((product) => product.status === "Active")
        .map((product) => (
          <div
            key={product.id}
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                productId: String(product.id),
              }));
              setShowDropdown(false);
            }}
            className="px-3 py-2 text-sm hover:bg-teal-50 cursor-pointer truncate"
          >
            {product.product_name}
          </div>
        ))}
    </div>
  )}
</div>
            {errors.productId && (
              <p className="text-red-500 text-sm">{errors.productId}</p>
            )}
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Rating *
            </label>
            <div className="flex items-center gap-2">
              {renderStars(formData.rating, true)}
              <span className="ml-2 text-sm text-gray-600">
                ({formData.rating}/5)
              </span>
            </div>
            {errors.rating && (
              <p className="text-red-500 text-sm">{errors.rating}</p>
            )}
          </div>

          <div className="space-y-3">
            <label
              htmlFor="comment"
              className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"
            >
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Review Comment *
            </label>
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleInputChange}
              placeholder="Enter review comment..."
              rows={4}
              className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 resize-none ${
                errors.comment
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-orange-200"
              }`}
              disabled={isLoading}
              maxLength={500}
            />
            <div className="flex justify-between items-center">
              {errors.comment ? (
                <p className="text-red-500 text-sm">{errors.comment}</p>
              ) : (
                <p className="text-xs text-gray-600">
                  Share your experience with this product
                </p>
              )}
              <span className="text-xs text-gray-600 font-medium">
                {formData.comment.length}/500
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="Active"
                  checked={formData.status === "Active"}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div
                  className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                    formData.status === "Active"
                      ? "border-green-500 bg-green-500"
                      : "border-gray-300 bg-white group-hover:border-green-400"
                  }`}
                >
                  {formData.status === "Active" && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Active
                </span>
              </label>

              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="Inactive"
                  checked={formData.status === "Inactive"}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div
                  className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                    formData.status === "Inactive"
                      ? "border-red-500 bg-red-500"
                      : "border-gray-300 bg-white group-hover:border-red-400"
                  }`}
                >
                  {formData.status === "Inactive" && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Inactive
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-4 pt-8 pb-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-white bg-opacity-80 text-gray-700 rounded-xl hover:bg-opacity-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400 font-semibold border border-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-teal-400 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : review ? (
                "Update Review"
              ) : (
                "Create Review"
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ReviewModal;